import { useEffect } from 'react';
import { useSetState } from 'react-use';
import { useParams } from 'react-router';
import { map } from 'lodash';
import { useNavigate } from 'react-router';

//Components
import { Grid, Link, Tabs } from '@ts/storybook';
import Error403 from '@components/Errors/403';
import * as Misc from '@components/Misc';

// Business
import Items from '@pages/DataBase/Flavors/Items/Items';
import KpiResults from '@pages/DataBase/Configuration/Kpis/KpiMetrics';
import Assets from '@pages/DataBase/Flavors/Assets/Assets';
import WipParts from '@pages/DataBase/Flavors/WipParts/WipParts';
import Processes from '@pages/DataBase/Flavors/Processes/Processes';
import Livestocks from '@pages/DataBase/Flavors/Livestock/Livestock';
import EventTypes from '@pages/DataBase/EventTypes/EventTypes';
import TaskTypes from '@pages/DataBase/TaskTypes/TaskTypes';
import EventGenerationRules from '@pages/DataBase/EventGenerationRules/EventGenerationRules';
import WarningRules from '@pages/DataBase/Warnings/WarningRules';
import GroupTemplates from '@pages/DataBase/GroupTemplate/GroupTemplates';
import Vehicles from '@pages/DataBase/Flavors/Vehicles/Vehicles';
import ItemsInCustody from '@pages/DataBase/Flavors/ItemInCustody/ItemsInCustody';
import ItemTypes from '@pages/DataBase/Flavors/ItemTypes/ItemTypes';
import Locations from '@pages/DataBase/Flavors/Locations/Locations';
import People from '@pages/DataBase/Flavors/People/People';
import Labels from '@pages/DataBase/Configuration/Labels/Labels';
import Images from '@pages/DataBase/Documents/Documents';
import Layers from '@pages/DataBase/Layers/Layers';
import Views from '@pages/DataBase/Views/Views';
import IndicatorTypes from '@pages/DataBase/Status/StatusIndicators';
import KPIOperations from '@pages/DataBase/Configuration/Kpis/KpiOperations';
import KPIs from '@pages/DataBase/Configuration/Kpis/KPIs';
import KPIReports from '@pages/DataBase/Configuration/Kpis/KpiReports';
import DistributionLists from '@pages/DataBase/Configuration/Distributions/DistributionLists';
import DistributionConfig from '@pages/DataBase/Configuration/Distributions/DistributionConfigurations';
import Channels from '@pages/DataBase/Channels/Channels';
import DeliveryChains from '@pages/DataBase/DeliveryChains/DeliveryChains';
import UniqueIDSeries from '@pages/DataBase/Configuration/UniqueIDSeries/UniqueIdSeries';
import Units from '@pages/DataBase/Units/Units';
import ItemRelations from '@pages/DataBase/Flavors/ItemRelations/ItemRelations';
import AllocationChecks from '@pages/DataBase/AllocationCheck/AllocationsCheck';

// Technical
import AppSettings from '@components/Utils/AppSettings/AppSettings';
import Devices from '@pages/DataBase/Flavors/Devices/Devices';
import DeviceTypes from '@pages/DataBase/DeviceTypes/DeviceTypes';
import ImportExport from '@components/Utils/ImportExport/ImportExportConfig';
import QuuppaSitePlanner from '@components/Utils/Quuppa/QuuppaSitePlanner';

// User
import AppProfiles from '@pages/DataBase/AppProfiles/AppProfiles';
import Users from '@pages/DataBase/Users/Users';
import Roles from '@pages/DataBase/Roles/Roles';
import Organizations from '@pages/DataBase/Organizations/Organizations';
import RoleAssignment from '@pages/DataBase/Organizations/RoleAssignment';
import RolesAcls from '@pages/DataBase/Roles/RoleAcls';
import MyRoles from '@pages/DataBase/Roles/MyRoles';
import Licenses from '@pages/DataBase/Licenses/Licenses';
import LicenseTypes from '@pages/DataBase/Licenses/LicenseTypes';

//View
import RuleSets from '@pages/DataBase/Rules/Rulesets';
import Categories from './DataBase/Configuration/Categories/Categories';
import GlobalDataGrid, { generateConfigComponent } from '@modules/dataGrid/GlobalDataGrid';
import { useAppSelector } from '@redux/store';
import { ELoginState } from '@interfaces/App.interface';

const ConfigPanelPage = props => {
  const { tab, page } = useParams();
  const navigate = useNavigate();
  const loginState = useAppSelector(state => state.global.loginState);

  let left;
  const [state, setState] = useSetState({
    activeTab: (left = tab) != null ? left : false,
    listTabs: [
      { label: 'Business Configuration', value: 'business' },
      { label: 'Users and permissions', value: 'access' },
      { label: 'Data visualization', value: 'views' },
      { label: 'Technical Configuration', value: 'technical' },
      { label: 'Subscription', value: 'subscription' },
    ],
  });

  useEffect(() => {
    if (loginState === ELoginState.LOGGED_OUT) {
      navigate('/login');
    }
    if (tab && state.activeTab !== tab) {
      setState({ activeTab: tab || false });
    } else {
      if (!tab && loginState === ELoginState.LOGGED_IN) _handleTabChange(null, 'business');
    }
  }, [tab]);

  const _handleTabChange = (event, tab) => {
    const title = state.listTabs.find(item => item.value === tab)?.label;
    localStorage.removeItem('lastTabClicked');
    navigate(`/config/${tab}`, { state: { title, resetHistoryLevel: 1 } });
  };

  const _renderContents = () => {
    const contents = (() => {
      const combineProps = { ...props, page: page || 'panel' };
      switch (state.activeTab) {
        case 'business':
          return <BusinessGrid {...combineProps} />;
        case 'access':
          return <UserGrid {...combineProps} />;
        case 'views':
          return <ViewGrid {...combineProps} />;
        case 'technical':
          return <TechnicalGrid {...combineProps} />;
        case 'subscription':
          return <SubscriptionGrid {...combineProps} />;
      }
    })();
    return contents;
  };

  return (
    <Tabs id="config" sticky onChange={_handleTabChange} value={state.activeTab} tabs={state.listTabs} pointerEvents={true}>
      {_renderContents()}
    </Tabs>
  );
};

interface IRow {
  title: string;
  value: string;
  component?: any;
}
interface IConfigRow {
  [key: string]: IRow;
}

interface IConfigOptions {
  [key: string]: {
    title: string;
    rows: IConfigRow;
  };
}
const BusinessGrid = props => {
  const navigate = useNavigate();

  const onClickLink = (routePath, title) => {
    localStorage.removeItem('lastTabClicked');
    navigate(routePath, { state: { title, resetHistoryLevel: 2 } });
  };

  const colsData: IConfigOptions = {
    assets: {
      title: 'Items',
      rows: {
        items: {
          title: 'All items',
          value: 'items',
          component: Items,
        },
        assets: {
          title: 'Assets',
          value: 'assets',
          component: Assets,
        },
        devices: {
          title: 'Devices',
          value: 'devices',
          component: Devices,
        },
        custody: {
          title: 'Items in custody',
          value: 'custody',
          component: ItemsInCustody,
        },
        livestocks: {
          title: 'Livestock',
          value: 'livestocks',
          component: Livestocks,
        },
        locations: {
          title: 'Locations',
          value: 'locations',
          component: Locations,
        },
        people: {
          title: 'People',
          value: 'people',
          component: People,
        },
        processes: {
          title: 'Processes',
          value: 'processes',
          component: Processes,
        },
        vehicles: {
          title: 'Vehicles',
          value: 'vehicles',
          component: Vehicles,
        },
        wip: {
          title: 'Work in process items',
          value: 'wip',
          component: WipParts,
        },
      },
    },
    workflow: {
      title: 'Workflow and tasks',
      rows: {
        itemTypes: {
          title: 'Item types',
          value: 'itemTypes',
          component: ItemTypes,
        },
        itemRelations: {
          title: 'Item relations',
          value: 'itemRelations',
          component: ItemRelations,
        },
        eventTypes: {
          title: 'Event Types',
          value: 'eventTypes',
          component: EventTypes,
        },
        taskTypes: {
          title: 'Task types',
          value: 'taskTypes',
          component: TaskTypes,
        },
        eventGenerationRules: {
          title: 'Event generation rules',
          value: 'eventGenerationRules',
          component: EventGenerationRules,
        },
        /* warnings: {
            title: 'Warnings',
            value: 'warnings',
            component: Warnings,
          }, */
        warningRules: {
          title: 'Warning rules',
          value: 'warningRules',
          component: WarningRules,
        },
        indicatorTypes: {
          title: 'Status indicators',
          value: 'indicatorTypes',
          component: IndicatorTypes,
        },
        allocationChecks: {
          title: 'Allocations Check',
          value: 'allocationChecks',
          component: AllocationChecks,
        },
        groupTemplates: {
          title: 'Group templates',
          value: 'groupTemplates',
          component: GroupTemplates,
        },
      },
    },
    other: {
      title: 'Other',
      rows: {
        labels: {
          title: 'Labels and groups',
          value: 'labels',
          component: Labels,
        },
        categories: {
          title: 'Categories',
          value: 'categories',
          component: Categories,
        },
        distLists: {
          title: 'Distribution Lists',
          value: 'distLists',
          component: DistributionLists,
        },
        distConfig: {
          title: 'Distribution Configuration',
          value: 'distConfig',
          component: DistributionConfig,
        },
        uniqueIdSerie: {
          title: 'Unique ID Series',
          value: 'uniqueIdSerie',
          component: UniqueIDSeries,
        },
        documents: {
          title: 'Documents/Images',
          value: 'documents',
          component: Images,
        },
      },
    },
  };

  const _renderContents = () => {
    const selected = colsData.assets.rows[_active] || colsData.workflow.rows[_active] || colsData.other.rows[_active];
    if (!selected) return _renderColumns();
    const Component = selected.component;
    return <Component {...props} />;
  };

  const _renderColumns = () => {
    return (
      <Grid container className="justify-center" spacing={1}>
        {map(colsData, (column, key) => (
          <Grid key={key}>
            <Misc.PaperWithPadding className="w-[350px]">
              <Misc.PaperSectionTitle>{column.title}</Misc.PaperSectionTitle>
              <br />
              {map(column.rows, row => (
                <div key={row.value} className="m-2.5">
                  <Link
                    id={'config.' + row.value}
                    onClick={() => onClickLink(`/config/business/${row.value}`, row.title)}
                    stProps={{ typography: { className: '!text-xl' } }}
                  >{`${row.title}`}</Link>
                  <br />
                </div>
              ))}
            </Misc.PaperWithPadding>
          </Grid>
        ))}
      </Grid>
    );
  };

  let left;
  const _active = (left = props.page) != null ? left : 'panel';
  return <>{_renderContents()}</>;
};

const UserGrid = props => {
  const navigate = useNavigate();

  const onClickLink = (routePath, title) => {
    localStorage.removeItem('lastTabClicked');
    navigate(routePath, { state: { title, resetHistoryLevel: 2 } });
  };

  const colsData = {
    usersAndPermissions: [
      { title: 'My organizations', value: 'organizations' },
      { title: 'Users', value: 'users' },
      { title: 'User roles', value: 'roles' },
      { title: 'Roles assignment', value: 'organizationsAcls' },
      { title: 'Roles privileges', value: 'rolesAcls' },
    ],
  };
  const _renderContents = () => {
    const contents = (() => {
      switch (_active) {
        case 'appProfiles':
          return <AppProfiles {...Object.assign({}, props)} />;
        case 'organizations':
          return <Organizations {...Object.assign({}, props)} />;
        case 'organizationsAcls':
          return <RoleAssignment {...Object.assign({}, props)} />;
        case 'rolesAcls':
          return <RolesAcls {...Object.assign({}, props)} />;
        case 'myRoles':
          return <MyRoles {...Object.assign({}, props)} />;
        case 'licenses':
          return <Licenses {...Object.assign({}, props)} />;
        case 'roles':
          return <Roles {...Object.assign({}, props)} />;
        case 'users':
          return <Users {...Object.assign({}, props)} />;
        case 'role':
          return <Error403 />;
        default:
          return _renderColumns();
      }
    })();
    return <>{contents}</>;
  };

  const _renderColumns = () => {
    return (
      <Grid container sx={{ justifyContent: 'center' }} spacing={1}>
        <Grid>
          <Misc.PaperWithPadding>
            <Misc.PaperSectionTitle>{'Users and permissions'}</Misc.PaperSectionTitle>
            <br />
            {colsData.usersAndPermissions.map(a => (
              <div key={a.value} className="w-[350px] m-2.5">
                <Link
                  id={'config.' + a.value}
                  onClick={() => onClickLink(`/config/access/${a.value}`, a.title)}
                  stProps={{ typography: { className: '!text-xl' } }}
                >{`${a.title}`}</Link>
                <br />
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
      </Grid>
    );
  };
  let left;
  const _active = (left = props.page) != null ? left : 'panel';
  return <>{_renderContents()}</>;
};

const ViewGrid = props => {
  const navigate = useNavigate();

  const onClickLink = (routePath, title) => {
    localStorage.removeItem('lastTabClicked');
    navigate(routePath, { state: { title, resetHistoryLevel: 2 } });
  };

  const colsData = {
    views: [
      { title: 'Views', value: 'views' },
      { title: 'Layers', value: 'layers' },
      {
        title: 'Selection and visualization rulesets',
        value: 'rulesets',
      },
      { title: '', value: 'blankLine' },
      { title: 'KPI Operations', value: 'kpiOperations' },
      { title: 'KPIs', value: 'kpis' },
      { title: 'KPI Reports', value: 'kpiReports', disabled: true },
      /* { title: 'KPI Results', value: 'itemKPIs' }, */
    ],
  };
  const _renderContents = () => {
    const contents = (() => {
      switch (_active) {
        case 'documents':
          return <Images {...Object.assign({}, props)} />;
        /*  case 'geometries':
          return <Geometries {...Object.assign({}, props)} />; */
        case 'layers':
          return <Layers {...Object.assign({}, props)} />;
        case 'views':
          return <Views {...Object.assign({}, props)} />;
        /*         case 'viewTypes':
          return <ViewTypes {...Object.assign({}, props)} />; */
        case 'rulesets':
          return <RuleSets {...Object.assign({}, props)} />;
        case 'kpiOperations':
          return <KPIOperations {...Object.assign({}, props)} />;
        case 'kpis':
          return <KPIs {...Object.assign({}, props)} />;
        case 'kpiReports':
          return <KPIReports {...Object.assign({}, props)} />;
        case 'itemKPIs':
          return <KpiResults {...Object.assign({}, props)} />;
        default:
          return _renderColumns();
      }
    })();
    return <>{contents}</>;
  };

  const _renderColumns = () => {
    return (
      <Grid container sx={{ justifyContent: 'center' }} spacing={1}>
        <Grid>
          <Misc.PaperWithPadding>
            <Misc.PaperSectionTitle>{'Data visualization'}</Misc.PaperSectionTitle>
            <br />
            {colsData.views.map(a => (
              <div key={a.value} className="w-[350px] m-2.5">
                {a.value === 'blankLine' ? (
                  <span></span>
                ) : (
                  <Link
                    id={'config.' + a.value}
                    disabled={a.disabled}
                    onClick={() => onClickLink(`/config/views/${a.value}`, a.title)}
                    stProps={{ typography: { className: '!text-xl' } }}
                  >{`${a.title}`}</Link>
                )}
                <br />
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
      </Grid>
    );
  };

  let left;
  const _active = (left = props.page) != null ? left : 'panel';
  return <>{_renderContents()}</>;
};

const TechnicalGrid = props => {
  const navigate = useNavigate();

  const onClickLink = (routePath, title) => {
    localStorage.removeItem('lastTabClicked');
    navigate(routePath, { state: { title, resetHistoryLevel: 2 } });
    // act.oldgoTo(routePath, { title, resetHistoryLevel: 2 });
  };

  const colsData = {
    config: [
      { title: 'Application settings', value: 'appSettings' },
      { title: 'Devices', value: 'devices' },
      { title: 'Device types', value: 'deviceTypes' },
      { title: 'Units', value: 'units' },
      { title: 'Export config', value: 'exportConfig' },
    ],
    wizards: [
      { title: 'RTLS configurator', value: 'gateway' },
      { title: 'uRTLS Simulator', value: 'urtlswizard' },
      {
        title: 'WAT infrastructure configurator',
        value: 'watInfra',
      },
      { title: 'WAT motes configurator', value: 'watMotes' },
      {
        title: 'RFID readers configurator',
        value: 'rfidReaders',
      },
      {
        title: 'Quuppa Site Planner',
        value: 'quuppaSitePlanner',
      },
    ],
    interfaces: [
      {
        title: 'IT systems/applications',
        value: 'itEquipments',
      },
      { title: 'Export of readings', value: 'readingExport' },
      { title: 'Channel types', value: 'channelTypes' },
      { title: 'Channels', value: 'channels' },
      { title: 'Delivery chains', value: 'deliveryChains' },
    ],
  };
  const _renderContents = () => {
    const contents = (() => {
      switch (_active) {
        case 'panel':
          return _renderColumns();
        case 'appSettings':
          return <AppSettings {...Object.assign({}, props)} />;
        case 'devices':
          return <Devices {...Object.assign({}, props)} />;
        case 'deviceTypes':
          return <DeviceTypes {...Object.assign({}, props)} />;
        case 'units':
          return <Units {...Object.assign({}, props)} />;
        case 'exportConfig':
          return <ImportExport {...Object.assign({}, props)} />;
        case 'channelTypes':
          return <GlobalDataGrid configComponent={generateConfigComponent('channelType')} />;
        case 'channels':
          return <Channels {...Object.assign({}, props)} />;
        case 'deliveryChains':
          return <DeliveryChains {...Object.assign({}, props)} />;
        case 'quuppaSitePlanner':
          return <QuuppaSitePlanner {...Object.assign({}, props)} />;
        default:
          return <Error403 />;
      }
    })();
    return <>{contents}</>;
  };

  const _renderColumns = () => {
    return (
      <Grid container sx={{ justifyContent: 'center' }} spacing={1}>
        <Grid>
          <Misc.PaperWithPadding className="w-[350px]">
            <Misc.PaperSectionTitle>{'Configuration'}</Misc.PaperSectionTitle>
            <br />
            {colsData.config.map(a => (
              <div key={a.value} className="m-2.5">
                <Link
                  id={'config.' + a.value}
                  onClick={() => onClickLink(`/config/technical/${a.value}`, a.title)}
                  stProps={{ typography: { className: '!text-xl' } }}
                >{`${a.title}`}</Link>
                <br />
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
        <Grid>
          <Misc.PaperWithPadding className="w-[350px]">
            <Misc.PaperSectionTitle>{'Wizards'}</Misc.PaperSectionTitle>
            <br />
            {colsData.wizards.map(a => (
              <div key={a.value} className="m-2.5">
                <Link
                  id={'config.' + a.value}
                  onClick={() => onClickLink(`/config/technical/${a.value}`, a.title)}
                  stProps={{ typography: { className: '!text-xl' } }}
                >{`${a.title}`}</Link>
                <br />
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
        <Grid>
          <Misc.PaperWithPadding className="w-[350px]">
            <Misc.PaperSectionTitle>{'Interfaces with other systems'}</Misc.PaperSectionTitle>
            <br />
            {colsData.interfaces.map(a => (
              <div key={a.value} className="m-2.5">
                <Link
                  id={'config.' + a.value}
                  onClick={() => onClickLink(`/config/technical/${a.value}`, a.title)}
                  stProps={{ typography: { className: '!text-xl' } }}
                >{`${a.title}`}</Link>
                <br />
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
      </Grid>
    );
  };
  let left;
  const _active = (left = props.page) != null ? left : 'panel';
  return <>{_renderContents()}</>;
};

const SubscriptionGrid = props => {
  const navigate = useNavigate();

  const onClickLink = (routePath, title) => {
    localStorage.removeItem('lastTabClicked');
    navigate(routePath, { state: { title, resetHistoryLevel: 2 } });
  };

  const colsData = {
    col1: [
      { title: 'Licenses', value: 'licenses' },
      { title: 'Licenses type', value: 'licenseTypes' },
      { title: 'Subscription', value: 'Subscription' },
    ],
  };

  const _renderContents = () => {
    const contents = (() => {
      switch (_active) {
        case 'licenses':
          return <Licenses {...Object.assign({}, props)} />;
        case 'licenseTypes':
          return <LicenseTypes {...Object.assign({}, props)} />;
        default:
          return _renderColumns();
      }
    })();
    return <>{contents}</>;
  };

  const _renderColumns = () => {
    return (
      <Grid container sx={{ justifyContent: 'center' }} spacing={1}>
        <Grid>
          <Misc.PaperWithPadding className="w-350px">
            <br />
            {colsData.col1.map(a => (
              <div key={a.value} className="m-2.5">
                <Link
                  id={'config.' + a.value}
                  onClick={() => onClickLink(`/config/subscription/${a.value}`, a.title)}
                  stProps={{ typography: { className: '!text-xl' } }}
                >{`${a.title}`}</Link>
              </div>
            ))}
          </Misc.PaperWithPadding>
        </Grid>
      </Grid>
    );
  };

  let left;
  const _active = (left = props.page) != null ? left : 'panel';
  return <>{_renderContents()}</>;
};

export default ConfigPanelPage;
